import React, { useState } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  IconButton,
  styled
} from "@mui/material";
import { Plus, X as CloseIcon, HelpCircle } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { motion } from "framer-motion";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";

const ModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  maxWidth: '95vw',
  backgroundColor: '#fff',
  borderRadius: '20px',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  padding: '32px',
  outline: 'none',
}));

const validationSchema = Yup.object({
  question: Yup.string().required("Question is required").min(5, "Question should be at least 5 characters long"),
  answer: Yup.string().required("Answer is required").min(10, "Answer should be at least 10 characters long"),
});

const AddFaq = ({ getFaq }) => {
  const [open, setOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      question: "",
      answer: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const data = {
        faqs: [
          {
            faq_title: values.question,
            faq_answer: values.answer,
          },
        ],
      };
      addFaqDetails(data);
    },
  });

  const addFaqDetails = async (data) => {
    try {
      await axiosInstance.post(
        `${apiEndPointsConfig.saveFaqDetails}`,
        data
      );
      formik.resetForm();
      getFaq();
      handleClose();
    } catch (error) {
      console.error("Error adding FAQ:", error);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Box>
      <Button
        variant="contained"
        startIcon={<Plus size={18} />}
        onClick={handleOpen}
        sx={{
          borderRadius: "12px",
          bgcolor: "#4F46E5",
          "&:hover": {
            bgcolor: "#4338CA",
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)'
          },
          textTransform: "none",
          fontSize: "14px",
          fontWeight: 700,
          px: 3,
          py: 1,
          boxShadow: 'none',
          transition: 'all 0.2s ease'
        }}
      >
        Add FAQ
      </Button>

      <Modal open={open} onClose={handleClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <ModalContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ bgcolor: '#EEF2FF', p: 1, borderRadius: '10px', color: '#4F46E5' }}>
                  <Plus size={20} />
                </Box>
                <Typography variant="h6" fontWeight={800} color="#111827">New FAQ</Typography>
              </Box>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon size={18} color="#6B7280" />
              </IconButton>
            </Box>

            <form onSubmit={formik.handleSubmit}>
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="caption" fontWeight={700} color="#6B7280" sx={{ mb: 1, display: 'block', textTransform: 'uppercase' }}>
                  Question
                </Typography>
                <TextField
                  name="question"
                  fullWidth
                  placeholder="e.g. How do I start a campaign?"
                  variant="outlined"
                  value={formik.values.question}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.question && Boolean(formik.errors.question)}
                  helperText={formik.touched.question && formik.errors.question}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: '12px',
                      bgcolor: '#F9FAFB',
                      fontSize: '14px',
                      "& fieldset": { borderColor: '#E5E7EB' },
                      "&.Mui-focused fieldset": { borderColor: '#4F46E5' }
                    }
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" fontWeight={700} color="#6B7280" sx={{ mb: 1, display: 'block', textTransform: 'uppercase' }}>
                  Answer
                </Typography>
                <TextField
                  name="answer"
                  fullWidth
                  placeholder="The detailed resolution..."
                  variant="outlined"
                  multiline
                  rows={5}
                  value={formik.values.answer}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.answer && Boolean(formik.errors.answer)}
                  helperText={formik.touched.answer && formik.errors.answer}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: '12px',
                      bgcolor: '#F9FAFB',
                      fontSize: '14px',
                      "& fieldset": { borderColor: '#E5E7EB' },
                      "&.Mui-focused fieldset": { borderColor: '#4F46E5' }
                    }
                  }}
                />
              </Box>

              <Button
                variant="contained"
                fullWidth
                type="submit"
                sx={{
                  bgcolor: "#4F46E5",
                  borderRadius: '12px',
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: 'none',
                  "&:hover": { bgcolor: "#4338CA" }
                }}
              >
                Create FAQ
              </Button>
            </form>
          </ModalContainer>
        </motion.div>
      </Modal>
    </Box>
  );
};

export default AddFaq;
